import React from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Line, RegularPolygon } from 'react-konva';
import { Html } from 'react-konva-utils';

import { onFocusSelect } from '@/hooks';
import { cleanNumberInput } from '@/utils/input';

import { dimensionLines } from '../colors';
import { useKonva } from '../KonvaContext';
import { Box } from '../types';

const DIMENSION_TEXT_SIZE = 16;
const DIMENSION_LINE_TRIANGLE_SIZE = 28;
const textPadding = 2;

type LinePrimitiveProps = {
  value: number;
  min: number;
  max: number;
  direction: 'horizontal' | 'vertical';
  box: Box;
  options?: LineOptions;
  onSubmit: (value: number) => void;
};

type LineOptions = {
  textSize?: number;
  color?: string;
  padding?: number;
  triangleSize?: number;
  altTriangle?: boolean;
};

export const LineInput = ({
  value,
  min,
  max,
  direction,
  box,
  options,
  onSubmit,
}: LinePrimitiveProps) => {
  const {
    textSize = DIMENSION_TEXT_SIZE,
    color = dimensionLines.frame,
    padding = textPadding,
    triangleSize = DIMENSION_LINE_TRIANGLE_SIZE,
    altTriangle = false,
  } = options || {};
  const [state, setState] = React.useState(String(value));
  const cancelledRef = React.useRef(false);
  const { t } = useTranslation();
  const { stageRef } = useKonva();
  const stageScale = stageRef.current?.getAttr('scaleX') || 1;

  const isVertical = direction === 'vertical';
  const length = isVertical ? box.height : box.width;

  // Triangle positions
  const triangle1 = {
    x: altTriangle ? triangleSize * 0.6 : triangleSize / 4,
    y: 0,
    rotation: isVertical ? (altTriangle ? 270 : 90) : altTriangle ? 270 : 90,
  };
  const triangle2 = {
    x: length - triangleSize / (altTriangle ? 2 : 4),
    y: 0,
    rotation: isVertical ? (altTriangle ? 90 : 270) : altTriangle ? 90 : 270,
  };

  const textInputWidth = 3 * textSize; // 4 chars is a good width
  const textBoxY = textSize / 2;
  let textBoxX = 0;
  if (isVertical) {
    textBoxX = box.height / 2 - textInputWidth / 2;
  } else {
    textBoxX = box.width / 2 - textInputWidth / 2;
  }

  // Rectangle for text background
  const rectX = textBoxX - padding;
  const rectY = isVertical
    ? textBoxY + padding * 2 - textInputWidth / 2
    : textBoxY + padding * 2;
  const rectWidth = textInputWidth + padding * 2;
  const rectHeight = textSize + padding * 2;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState(String(cleanNumberInput(e.target.value)));
  };

  const triggerSubmit = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      setState(String(value));
      return;
    }

    const parsed = Number(state);
    if (!parsed) {
      setState(String(value));
      return;
    }

    const constrainedValue = Math.max(min, Math.min(parsed, max));
    setState(String(constrainedValue));

    if (constrainedValue !== value) {
      onSubmit(constrainedValue);
    }
  };

  return (
    <Group x={box.x} y={box.y}>
      <Line
        points={isVertical ? [0, 0, 0, length] : [0, 0, length, 0]}
        stroke={color}
        strokeWidth={2}
        listening={false}
      />
      <Group
        x={0}
        y={0}
        scaleY={isVertical ? -1 : 1}
        rotation={isVertical ? 90 : 0}
      >
        <Html
          groupProps={{
            x: rectX,
            y: rectY,
            width: rectWidth,
            height: rectHeight,
            fill: color,
            scaleY: isVertical ? 1 : -1,
          }}
        >
          <input
            style={{
              color: 'black',
              fontSize: textSize,
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              border: '1px solid black',
              width: rectWidth,
              height: rectHeight + padding * 2,
              textAlign: 'center',
              transform: `scale(${1 / stageScale}) ${
                isVertical ? 'rotate(90deg)' : 'rotate(0deg)'
              }`,
              transformOrigin: 'center',
            }}
            value={state}
            onChange={onChange}
            onBlur={triggerSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                cancelledRef.current = true;
                e.currentTarget.blur();
              }
            }}
            onFocus={onFocusSelect}
            type="text"
            inputMode="numeric"
            title={t('reseller.windowEditor.dimensionInput', {
              min: Math.ceil(min),
              max: Math.floor(max),
            })}
            aria-label={t('reseller.windowEditor.dimensionInput', {
              min: Math.ceil(min),
              max: Math.floor(max),
            })}
          />
        </Html>
        <RegularPolygon
          x={triangle1.x}
          y={triangle1.y}
          sides={3}
          radius={triangleSize * 0.6}
          fill={color}
          rotation={triangle1.rotation}
          listening={false}
        />
        <RegularPolygon
          x={triangle2.x}
          y={triangle2.y}
          sides={3}
          radius={triangleSize * 0.6}
          fill={color}
          rotation={triangle2.rotation}
          listening={false}
        />
      </Group>
    </Group>
  );
};
